import {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import type {ChangeEvent, ComponentPropsWithRef, ComponentType, ReactNode} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import dayjs from 'dayjs';
import type {Dayjs} from 'dayjs';
import {Alert, Button, Checkbox, DatePicker, Empty, Flex, Form, Input, Popconfirm, Select, Switch, Table, Tag, TimePicker, Tooltip, Tour, Typography, type CheckboxChangeEvent, type TableColumnsType} from 'antd';
import {
    CheckCircleOutlined,
    CheckOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    FileTextOutlined,
    FolderAddOutlined,
    InboxOutlined,
    PlusOutlined,
    SearchOutlined,
    ShopOutlined,
    SwapOutlined,
    UploadOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import {DragAndDropUploadForPdfOrXml} from 'diferpan-common-web'
import {useAppMessage} from '@providers'
import {
    DICA_JANELAS_ENTREGA,
    PASSO_MINUTOS_ENTREGA,
    buscarFilialDaUf,
    buscarUfDaFilial,
    extractNumbers,
    isArquivoXml,
    lerDestinatarioDoArquivoXml,
    lerNotaFiscalDoXml,
    lerNumeroConhecimentoDoXml,
    lerTextoDoArquivo,
    mapFilesToDto,
    resolverHorasEMinutosDesabilitados,
    resolverMensagemErro,
    validarHorarioEntrega,
} from '@utils';
import type {DestinatarioNotaFiscal} from '@utils';
import { analisarAnexosAgendamento } from '@utils/anexos-agendamento'
import {classificarConferenciaUf, resolverMarcaConferenciaNota} from '@utils/nota-fiscal-filial'
import type {ConferenciaUfNota} from '@utils/nota-fiscal-filial'
import {useBuscarXmlNfeAgendaExterna, useCadastrarAgendamentoMutation, useParceirosQuery} from "@queries";
import {useGlobalFilial, useGlobalUser, useGuidedTour} from "@hooks";
import {GuidedTour} from "@components";
import './style.scss';
import {TRANSPORT_OPTIONS} from "@constants";
import type {AgendaExternaCadastroRequest, AgendaExternaCifDto, AgendaExternaFobDto, Identificador} from '../../types';

const {Text} = Typography;

interface UploadRef {
    clearFiles(): void;
}

// TODO: Claude 11/09/2026 | remover as assercoes de target quando @rc-component/tour declarar target como (() => HTMLElement | null);
// hoje a uniao declarada e HTMLElement | (() => HTMLElement) | null | (() => null), que nenhuma funcao devolvendo HTMLElement | null satisfaz
type ArquivoUpload = File & {uid?: string; originFileObj?: File};

interface ArquivoRemovido {
    uid: string;
}

interface ConferenciaNotaLista {
    marca: ConferenciaUfNota;
    ufDestinatario: string | null;
}

interface AgendamentoFormValues {
    horaEntrega: Dayjs | null;
    codeSupplierOrTransport: Identificador;
    tipoCaminhao: string;
    numeroConhecimento?: number | string;
    chaveNfe: string;
    razaoSocialFornecedor: string;
    dataEmissao: Dayjs | null;
    volumeCarga: string | number;
}

// A diferpan-common-web declara os wrappers DragAndDropUploadFor* com BaseUploadProps,
// que nao inclui maxCount — mas o runtime repassa a prop por spread para DragAndDropUpload
// (dist/esm/components/DragAndDropUpload/index.js:387), onde ela vale.
// TODO: Claude 11/09/2026 | remover quando a lib declarar maxCount no contrato dos wrappers
const UploadPdfOuXml = DragAndDropUploadForPdfOrXml as ComponentType<
    ComponentPropsWithRef<typeof DragAndDropUploadForPdfOrXml> & {maxCount?: number}
>;

const requestDto: AgendaExternaCadastroRequest = {
    agendas: []
};

const MENSAGEM_FILIAL_SEM_UF = 'Não foi possível determinar a UF da filial selecionada, então o destinatário da Nota Fiscal não pode ser conferido.';

const ETAPAS_AGENDAMENTO = ['Dados do agendamento', 'Nota fiscal', 'CIF/FOB', 'Finalizar'] as const;

const FORMATO_DATA = 'DD/MM/YYYY';

const FORMATO_HORA = 'HH:mm';

const normalizarSomenteDigitos = (valor: unknown) => extractNumbers(valor) ?? '';

const converterNumeroConhecimento = (valor: number | string | undefined) => (valor === undefined || valor === '' ? undefined : Number(valor));

const somarVolumes = (notas: AgendaExternaCifDto[]) => notas.reduce((total, nota) => total + Number(nota.volumeCarga || 0), 0);

const pluralizar = (quantidade: number, singular: string, plural: string) => (quantidade === 1 ? singular : plural);

const contarMarcas = (conferencias: Record<string, ConferenciaNotaLista>, marca: ConferenciaUfNota) =>
    Object.values(conferencias).filter(conferencia => conferencia.marca === marca).length;

const renderMarcadorObrigatorio = (rotulo: ReactNode, {required}: {required: boolean}) => (
    <>
        {rotulo}
        {required && <span className="form-field__obrigatorio" aria-hidden="true">*</span>}
    </>
);

export const AgendamentoPage = () => {
    const message = useAppMessage();
    const navigate = useNavigate();
    const {date} = useParams();
    const [user] = useGlobalUser();
    const [filial, definirFilial] = useGlobalFilial();
    const NFUploadRef = useRef<UploadRef | null>(null);
    const CTOUploadRef = useRef<UploadRef | null>(null);
    const refSelectFornecedor = useRef<HTMLDivElement | null>(null);
    const refHoraEntrega = useRef<HTMLDivElement | null>(null);
    const refTipoCaminhao = useRef<HTMLDivElement | null>(null);
    const refSwitchCifFob = useRef<HTMLDivElement | null>(null);
    const refUploadConhecimento = useRef<HTMLDivElement | null>(null);
    const refUploadNotaFiscal = useRef<HTMLDivElement | null>(null);
    const refChaveNfe = useRef<HTMLDivElement | null>(null);
    const refBotaoAdicionarNf = useRef<HTMLDivElement | null>(null);
    const refTabelaNotas = useRef<HTMLDivElement | null>(null);
    const refBotaoAdicionarCifFob = useRef<HTMLDivElement | null>(null);
    const refBotaoFinalizar = useRef<HTMLDivElement | null>(null);
    const inputXmlConferenciaRef = useRef<HTMLInputElement | null>(null);
    const [form] = Form.useForm<AgendamentoFormValues>();
    const chaveNfe = Form.useWatch('chaveNfe', form);
    const parceiroSelecionado = Form.useWatch('codeSupplierOrTransport', form);
    const horaEntregaSelecionada = Form.useWatch('horaEntrega', form);
    const tipoCaminhaoSelecionado = Form.useWatch('tipoCaminhao', form);
    const [isFob, setIsFob] = useState(false);
    const [formRequestFobDto, setFormRequestFobDto] = useState<AgendaExternaFobDto | null>(null);
    const [formRequestGeralDto, setFormRequestGeralDto] = useState<AgendaExternaFobDto[]>([]);
    const [formRequestNotasDto, setFormRequestNotasDto] = useState<AgendaExternaCifDto[]>([]);
    const [formRequest, setFormRequest] = useState<AgendaExternaCadastroRequest>(requestDto);
    const [fobFiles, setFobFiles] = useState<ArquivoUpload[]>([]);
    const [cifFiles, setCifFiles] = useState<ArquivoUpload[]>([]);
    const [destinatarioNf, setDestinatarioNf] = useState<DestinatarioNotaFiscal | null>(null);
    const [chaveNfeFilialConfirmada, setChaveNfeFilialConfirmada] = useState<string | null>(null);
    const [cienciaUfDivergente, setCienciaUfDivergente] = useState<DestinatarioNotaFiscal | null>(null);
    const [conferenciasPorChave, setConferenciasPorChave] = useState<Record<string, ConferenciaNotaLista>>({});
    const [chaveNotaEmConferencia, setChaveNotaEmConferencia] = useState<string | null>(null);
    const parceirosQuery = useParceirosQuery({isFornecedor: user.isFornecedor, codigoUsuario: user.code});
    const codeSupplierOrTransportData = parceirosQuery.data ?? [];
    const rotuloParceiro = user.isFornecedor === true ? 'Transportadora' : 'Fornecedor';
    const placeholderSelectSupplierOrTransport = user.isFornecedor === true
        ? 'Selecione a transportadora'
        : 'Selecione o fornecedor';
    const [guiaProximoCifFobAberto, setGuiaProximoCifFobAberto] = useState(false);
    const [guiaProximoFinalizarAberto, setGuiaProximoFinalizarAberto] = useState(false);
    const [buttonMaisDadosLoading, setButtonMaisDadosLoading] = useState(false);
    const cadastrarMutation = useCadastrarAgendamentoMutation();
    const buscarXmlNfe = useBuscarXmlNfeAgendaExterna();

    const { tourAberto, abrirTour, fecharTour } = useGuidedTour('agendamento');

    const passosGuiaProximoCifFob = useMemo(() => [
        {
            title: 'Já adicionou todas as Notas Fiscais?',
            description: 'Se ainda houver mais notas, continue adicionando-as. Caso contrário, clique em "Adicionar CIF/FOB" para registrar essa operação na lista.',
            target: () => refBotaoAdicionarCifFob.current as HTMLElement,
        },
    ], []);

    const passosGuiaProximoFinalizar = useMemo(() => [
        {
            title: 'Adicionar mais ou Finalizar?',
            description: 'Se ainda houver mais CIF/FOB para registrar, repita o processo. Caso contrário, clique em "Finalizar Agendamento" para concluir.',
            target: () => refBotaoFinalizar.current as HTMLElement,
        },
    ], []);

    const passosBaseTour = [
        {
            title: 'Selecione o parceiro',
            description: 'Comece escolhendo a Transportadora ou o Fornecedor responsável pela entrega.',
            target: () => refSelectFornecedor.current as HTMLElement,
        },
        {
            title: 'Hora de entrega',
            description: 'Informe o horário previsto para a entrega na filial.',
            target: () => refHoraEntrega.current as HTMLElement,
        },
        {
            title: 'Tipo de caminhão',
            description: 'Escolha o tipo de caminhão usado no transporte. É permitido apenas um tipo por agendamento.',
            target: () => refTipoCaminhao.current as HTMLElement,
        },
        {
            title: 'Frete CIF ou FOB',
            description: 'Ative o seletor caso o frete seja FOB. Isso habilita o envio do Conhecimento de Frete.',
            target: () => refSwitchCifFob.current as HTMLElement,
        },
    ];

    const passoUploadConhecimento = {
        title: 'Conhecimento de Frete (FOB)',
        description: 'Anexe os arquivos XML e PDF do Conhecimento de Frete e informe o número.',
        target: () => refUploadConhecimento.current as HTMLElement,
    };

    const passosFinalTour = [
        {
            title: 'Nota Fiscal',
            description: 'Anexe os arquivos XML e PDF da Nota Fiscal.',
            target: () => refUploadNotaFiscal.current as HTMLElement,
        },
        {
            title: 'Chave de acesso',
            description: 'Digite os 44 dígitos da chave de acesso.',
            target: () => refChaveNfe.current as HTMLElement,
        },
        {
            title: 'Adicionar NF',
            description: 'Após preencher os dados da nota, clique aqui para adicioná-la à lista.',
            target: () => refBotaoAdicionarNf.current as HTMLElement,
        },
        {
            title: 'Lista de Notas Fiscais',
            description: 'As notas adicionadas aparecem aqui. Você pode adicionar quantas precisar.',
            target: () => refTabelaNotas.current as HTMLElement,
        },
        {
            title: 'Adicionar CIF/FOB',
            description: 'Quando terminar de adicionar as notas desta operação, clique aqui para registrá-la na lista.',
            target: () => refBotaoAdicionarCifFob.current as HTMLElement,
        },
        {
            title: 'Finalizar Agendamento',
            description: 'Após registrar todas as operações, clique aqui para concluir o agendamento.',
            target: () => refBotaoFinalizar.current as HTMLElement,
        },
    ];

    const passosTour = useMemo(
        () => (isFob
            ? [...passosBaseTour, passoUploadConhecimento, ...passosFinalTour]
            : [...passosBaseTour, ...passosFinalTour]),
        [isFob]
    );

    const validateNumeroConhecimento = (_: unknown, value: unknown) => {
        if (isFob && !value) {
            return Promise.reject(new Error('Campo obrigatório!'));
        }
        return Promise.resolve();
    };

    function extractCNPJ(chaveAcesso: string) {
        if (chaveAcesso.length !== 44) {
            message.error("A chave de acesso deve ter 44 dígitos.");
        }
        return chaveAcesso.substring(6, 20);
    }

    function extractNumeroNF(chaveAcesso: string) {
        if (chaveAcesso.length !== 44) {
            message.error("A chave de acesso deve ter 44 dígitos.");
        }
        return chaveAcesso.substring(25, 34);
    }

    useEffect(() => {
        if (parceirosQuery.error) {
            message.error("Erro ao buscar lista da Trasportadoras/Fornecedores:");
        }
    }, [parceirosQuery.error]);

    const anexosCif = analisarAnexosAgendamento(cifFiles);
    const anexosFob = analisarAnexosAgendamento(fobFiles);
    const isCifFile = anexosCif.valido;
    const isFobFile = anexosFob.valido;
    const dicaTipoFrete = isFob
        ? 'Frete FOB selecionado — o Conhecimento de Frete passa a ser exigido.'
        : 'Frete CIF selecionado — o Conhecimento de Frete não é exigido.';
    const infoTextNotaFiscal = `Arquivos XML e PDF da Nota Fiscal  (${isFob ? 'FOB' : 'CIF'})`;

    const chaveNfeAtual = String(chaveNfe ?? '');
    const ufFilialSelecionada = buscarUfDaFilial(filial);
    const isFilialSemUf = !ufFilialSelecionada;
    const ufDestinatarioNf = destinatarioNf?.chaveNfe === chaveNfeAtual ? destinatarioNf.uf : null;
    const estadoConferenciaUf = ufFilialSelecionada ? classificarConferenciaUf(ufDestinatarioNf, ufFilialSelecionada) : null;
    const isDestinatarioConferido = ufDestinatarioNf !== null;
    const isDestinatarioDivergente = estadoConferenciaUf === 'CONFLITO';
    const isDestinatarioConfere = estadoConferenciaUf === 'VALIDADO';
    const isChaveNfePreenchida = chaveNfeAtual.length > 0;
    const isFilialConfirmadaPeloUsuario = chaveNfeFilialConfirmada !== null && chaveNfeFilialConfirmada === chaveNfeAtual;
    const isConfirmacaoFilialPendente = !isDestinatarioConferido && !isFilialConfirmadaPeloUsuario;
    const isCienciaUfDivergenteRegistrada = isDestinatarioDivergente
        && cienciaUfDivergente?.chaveNfe === chaveNfeAtual
        && cienciaUfDivergente.uf === ufDestinatarioNf;
    const isCienciaUfDivergentePendente = isDestinatarioDivergente && !isCienciaUfDivergenteRegistrada;
    const isAdicionarNfBloqueado = isFilialSemUf || isCienciaUfDivergentePendente || (isChaveNfePreenchida && isConfirmacaoFilialPendente);
    const filialDoDestinatario = buscarFilialDaUf(ufDestinatarioNf);
    const isDestinatarioDeFilialConhecida = Boolean(filialDoDestinatario);
    const origemXmlNotaFiscal = cifFiles.find(isArquivoXml)?.name ?? 'XML obtido pela busca automática';
    const situacaoXmlDivergente = isCienciaUfDivergenteRegistrada ? 'ciência registrada' : 'aguardando sua ciência';

    const isDadosAgendamentoPreenchidos = Boolean(parceiroSelecionado) && Boolean(horaEntregaSelecionada) && Boolean(tipoCaminhaoSelecionado);
    const hasNotasNaLista = formRequestNotasDto.length > 0;
    const hasBlocosCifFob = formRequestGeralDto.length > 0;
    const notasEmBlocos = formRequestGeralDto.flatMap(bloco => bloco.notas ?? []);
    const totalNotas = formRequestNotasDto.length + notasEmBlocos.length;
    const totalVolumes = somarVolumes(formRequestNotasDto) + somarVolumes(notasEmBlocos);
    const totalReservadas = contarMarcas(conferenciasPorChave, 'RESERVADA_SEM_XML');
    const totalComCienciaUf = contarMarcas(conferenciasPorChave, 'CIENCIA_UF_DIVERGENTE');
    const totalConferidas = formRequestNotasDto.length - totalReservadas - totalComCienciaUf;

    const resolverEtapaAtual = () => {
        if (!isDadosAgendamentoPreenchidos) return 0;
        if (!hasNotasNaLista && !hasBlocosCifFob) return 1;
        if (!hasBlocosCifFob) return 2;
        return 3;
    };
    const etapaAtual = resolverEtapaAtual();

    const resolverMotivoBloqueioNf = () => {
        if (isFilialSemUf) return 'Selecione a filial novamente na tela de agendamentos para liberar Adicionar NF.';
        if (isDestinatarioDivergente) return `O XML anexado é de outra UF (${ufDestinatarioNf}): marque a ciência no aviso para liberar Adicionar NF.`;
        return `Confirme acima que esta NF é destinada à filial ${ufFilialSelecionada} para liberar Adicionar NF.`;
    };

    const resolverMarcaDaNota = (chaveNfe?: string) => conferenciasPorChave[chaveNfe ?? '']?.marca ?? 'CONFERIDA';

    const resolverUfDivergenteDaNota = (chaveNfe?: string) => conferenciasPorChave[chaveNfe ?? '']?.ufDestinatario ?? '';

    const resolverClasseLinhaNota = (chaveNfe?: string) => {
        switch (resolverMarcaDaNota(chaveNfe)) {
            case 'RESERVADA_SEM_XML':
                return 'linha-reservada';
            case 'CIENCIA_UF_DIVERGENTE':
                return 'linha-divergente';
            case 'CONFERIDA':
                return '';
        }
    };

    const resolverDicaListaNotas = () => {
        if (!hasNotasNaLista) return 'Nenhuma nota adicionada';
        const marcas = [
            totalConferidas > 0 && `${totalConferidas} ${pluralizar(totalConferidas, 'conferida', 'conferidas')} pelo XML`,
            totalReservadas > 0 && `${totalReservadas} ${pluralizar(totalReservadas, 'reservada', 'reservadas')} sob sua confirmação`,
            totalComCienciaUf > 0 && `${totalComCienciaUf} ${pluralizar(totalComCienciaUf, 'recebida', 'recebidas')} com ciência de UF divergente`,
        ].filter(Boolean).join(', ');
        return `${formRequestNotasDto.length} ${pluralizar(formRequestNotasDto.length, 'nota adicionada', 'notas adicionadas')} — ${marcas}`;
    };

    const preencherNotaFiscalPeloXml = async (arquivo: Blob) => {
        const xml = await lerTextoDoArquivo(arquivo);
        const nota = xml ? lerNotaFiscalDoXml(xml) : null;
        if (!nota) return;

        form.setFieldsValue({
            chaveNfe: nota.chaveNfe ?? undefined,
            razaoSocialFornecedor: nota.razaoSocialEmitente ?? undefined,
            dataEmissao: nota.dataEmissao ? dayjs(nota.dataEmissao) : undefined,
            volumeCarga: nota.volumeCarga ?? undefined,
        });

        setDestinatarioNf(nota.chaveNfe && nota.ufDestinatario ? {chaveNfe: nota.chaveNfe, uf: nota.ufDestinatario} : null);
        setCienciaUfDivergente(null);
    };

    const preencherConhecimentoPeloXml = async (arquivo: Blob) => {
        const xml = await lerTextoDoArquivo(arquivo);
        const numeroConhecimento = xml ? lerNumeroConhecimentoDoXml(xml) : null;
        if (numeroConhecimento !== null) {
            form.setFieldsValue({numeroConhecimento});
        }
    };

    const handleNfeGetMaisDados = async () => {
        setButtonMaisDadosLoading(true)
        try {
            const chaveNfe = form.getFieldValue('chaveNfe');
            if (!chaveNfe) return;
            const data = await buscarXmlNfe(chaveNfe);

            const xml = data.Document?.Document
            if (!xml) throw new Error('Resposta do Mais Dados sem XML da NFe.');

            const nota = lerNotaFiscalDoXml(xml);
            if (!nota) throw new Error('XML do Mais Dados sem infNFe.');

            form.setFieldsValue({
                razaoSocialFornecedor: nota.razaoSocialEmitente ?? undefined,
                dataEmissao: nota.dataEmissao ? dayjs(nota.dataEmissao) : undefined,
                volumeCarga: nota.volumeCarga ?? undefined,
            })

            setDestinatarioNf(nota.ufDestinatario ? {chaveNfe, uf: nota.ufDestinatario} : null)
            setCienciaUfDivergente(null)
        } catch (error) {
            message.error("Erro ao buscar informações do Mais Dados!")
            console.error(error)
        } finally {
            setButtonMaisDadosLoading(false)
        }
    };

    const handleFileUploadForCIF = (file: ArquivoUpload) => {
        setCifFiles((prevFiles) => [...prevFiles, file]);
        if (!isArquivoXml(file)) return;
        void preencherNotaFiscalPeloXml(file.originFileObj ?? file);
    };

    const handleFileUploadForFOB = (file: ArquivoUpload) => {
        setFobFiles((prevFiles) => [...prevFiles, file])
        if (!isArquivoXml(file)) return;
        void preencherConhecimentoPeloXml(file.originFileObj ?? file);
    }

    const handleFileDeleteForCIF = (fileToDelete: ArquivoRemovido) => {
        const updatedFiles = (files: ArquivoUpload[]) => files.filter(file => file.uid !== fileToDelete.uid);

        setCifFiles(updatedFiles);
        cleanFormNotFob();

    };

    const handleFileDeleteForFOB = (fileToDelete: ArquivoRemovido) => {
        const updatedFiles = (files: ArquivoUpload[]) => files.filter(file => file.uid !== fileToDelete.uid);

        setFobFiles(updatedFiles);
        cleanForm();
    };

    const limparConferenciaDestinatario = () => {
        setDestinatarioNf(null);
        setChaveNfeFilialConfirmada(null);
        setCienciaUfDivergente(null);
    }

    const cleanForm = () => {
        const saveFields = {
            horaEntrega: form.getFieldValue('horaEntrega'),
            tipoCaminhao: form.getFieldValue('tipoCaminhao'),
            codeSupplierOrTransport: form.getFieldValue('codeSupplierOrTransport'),
        };
        form.resetFields();
        form.setFieldsValue(saveFields);

        setCifFiles([]);
        setFobFiles([]);
        NFUploadRef.current?.clearFiles();
        CTOUploadRef.current?.clearFiles();
        limparConferenciaDestinatario();
    }

    const handleTipoFreteChange = (novoIsFob: boolean) => {
        setIsFob(novoIsFob);
        cleanForm();
        setFormRequestNotasDto([]);
        setConferenciasPorChave({});
        if (!novoIsFob) form.setFieldsValue({numeroConhecimento: undefined});
    };

    const cleanFormNotFob = () => {
        const saveFields = {
            horaEntrega: form.getFieldValue('horaEntrega'),
            tipoCaminhao: form.getFieldValue('tipoCaminhao'),
            numeroConhecimento: form.getFieldValue('numeroConhecimento'),
            codeSupplierOrTransport: form.getFieldValue('codeSupplierOrTransport'),
        };
        form.resetFields();
        form.setFieldsValue(saveFields);

        setCifFiles([]);
        NFUploadRef.current?.clearFiles();
        limparConferenciaDestinatario();
    }

    const handleTrocarFilial = () => navigate('/');

    const handleAgendarNaFilialDoDestinatario = () => {
        if (!filialDoDestinatario) return;
        definirFilial(filialDoDestinatario.value);
        navigate('/');
    };

    const handleTrocarXmlNotaFiscal = () => cleanFormNotFob();

    const handleConfirmacaoFilialChange = (evento: CheckboxChangeEvent) => {
        setChaveNfeFilialConfirmada(evento.target.checked ? chaveNfeAtual : null);
    };

    const handleCienciaUfDivergenteChange = (evento: CheckboxChangeEvent) => {
        setCienciaUfDivergente(evento.target.checked && ufDestinatarioNf ? {chaveNfe: chaveNfeAtual, uf: ufDestinatarioNf} : null);
    };

    const handleAnexarXmlNotaReservada = (chaveNfe?: string) => {
        if (!chaveNfe) return;
        setChaveNotaEmConferencia(chaveNfe);
        inputXmlConferenciaRef.current?.click();
    };

    const handleXmlConferenciaChange = async (evento: ChangeEvent<HTMLInputElement>) => {
        const arquivo = evento.target.files?.[0];
        const chaveNota = chaveNotaEmConferencia;
        evento.target.value = '';
        setChaveNotaEmConferencia(null);
        if (!arquivo || !chaveNota) return;

        if (isFilialSemUf) {
            message.error(MENSAGEM_FILIAL_SEM_UF);
            return;
        }

        const destinatario = await lerDestinatarioDoArquivoXml(arquivo);

        if (!destinatario) {
            message.error('Não foi possível ler o destinatário no XML selecionado.');
            return;
        }

        if (destinatario.chaveNfe !== chaveNota) {
            message.error('O XML selecionado é de outra Nota Fiscal.');
            return;
        }

        if (destinatario.uf !== ufFilialSelecionada) {
            message.warning(`O destinatário do XML é de ${destinatario.uf} e a filial selecionada é ${ufFilialSelecionada}. Para registrar a ciência, remova a nota e adicione-a de novo com este XML.`);
            return;
        }

        try {
            const arquivosDto = await mapFilesToDto([arquivo]);

            setFormRequestNotasDto(prevState => prevState.map(nota => nota.chaveNfe === chaveNota
                ? {...nota, cifFilesDto: [...(nota.cifFilesDto ?? []), ...arquivosDto]}
                : nota));
            setConferenciasPorChave(prevState => ({...prevState, [chaveNota]: {marca: 'CONFERIDA', ufDestinatario: destinatario.uf}}));
            message.success('Nota Fiscal conferida pelo XML.');
        } catch {
            message.error('Não foi possível anexar o XML selecionado.');
        }
    };

    const onFinish = async (values: AgendamentoFormValues) => {
        if (isFilialSemUf) {
            message.error(MENSAGEM_FILIAL_SEM_UF);
            return;
        }

        if (!estadoConferenciaUf) return;

        if (isCienciaUfDivergentePendente) {
            message.warning(`Marque a ciência de que esta Nota Fiscal é de ${ufDestinatarioNf} antes de adicioná-la na filial ${ufFilialSelecionada}.`);
            return;
        }

        if (isConfirmacaoFilialPendente) {
            message.warning(`Confirme que esta Nota Fiscal é destinada à filial ${ufFilialSelecionada} antes de adicioná-la.`);
            return;
        }

        const marcaConferencia = resolverMarcaConferenciaNota(estadoConferenciaUf);
        const horaEntrega = values.horaEntrega ?? dayjs();
        const fobFileDto = isFobFile ? await mapFilesToDto(fobFiles) : [];
        const cifFileDto = isCifFile ? await mapFilesToDto(cifFiles) : [];
        const isPdfFileCif = cifFileDto.filter(file => file.type === 'application/pdf').length !== 1;
        const isPdfFileFob = fobFileDto.filter(file => file.type === 'application/pdf').length !== 1;

        if (!isFob && (cifFileDto.length < 1 || fobFileDto.length !== 0)) {
            message.warning('Por conta do Emitente (CIF) é permitido somente 1 PDF e 1 XML');
            return;
        }

        if (isFob && (isPdfFileCif || isPdfFileFob)) {
            message.warning("Deve conter DOIS PDF. (NF, CTe)");
            return;
        }

        if (!isFob && isPdfFileCif) {
            message.warning("Deve conter UM PDF. (NF)");
            return;
        }

        setFormRequest({
            dataCriacao: dayjs()
                .format('YYYY-MM-DDTHH:mm:ss'),
            dataEntrega: dayjs(date, "DD-MM-YYYY")
                .hour(horaEntrega.hour())
                .minute(horaEntrega.minute())
                .second(0)
                .format('YYYY-MM-DDTHH:mm:ss'),
            transportadoraId: user.isFornecedor ? Number(values.codeSupplierOrTransport) : user.code ?? undefined,
            codigoFornecedor: user.isFornecedor ? user.code ?? undefined : Number(values.codeSupplierOrTransport),
            filialEntrega: Number(filial),
            tipoCaminhao: values.tipoCaminhao,
        })

        const numeroConhecimento = converterNumeroConhecimento(values.numeroConhecimento);

        setFormRequestFobDto({
            tipoConhecimento: isFob ? "FOB" : "CIF",
            numeroConhecimento,
            fobFilesDto: fobFileDto,
        });


        const updatedCifRequestDto = {
            chaveNfe: values.chaveNfe,
            numeroNota: Number(extractNumeroNF(values.chaveNfe)),
            cnpjFornecedor: extractCNPJ(values.chaveNfe),
            dataEmissao: values.dataEmissao?.format('YYYY-MM-DDTHH:mm:ss'),
            razaoSocialFornecedor: values.razaoSocialFornecedor,
            tipoConhecimento: isFob ? "FOB" : "CIF",
            volumeCarga: Number(values.volumeCarga),
            numeroConhecimento,
            cifFilesDto: cifFileDto,
        };

        try {
            setFormRequestNotasDto(prevState => [...prevState, updatedCifRequestDto]);
            setConferenciasPorChave(prevState => ({...prevState, [values.chaveNfe]: {marca: marcaConferencia, ufDestinatario: ufDestinatarioNf}}));
            message.success("Dados adicionados à lista com sucesso");
            cleanFormNotFob();
            setCifFiles([]);
            NFUploadRef.current?.clearFiles();
            setGuiaProximoCifFobAberto(true);
        } catch {
            message.error("Erro ao finalizar o agendamento");
        }
    }

    const handleNotaFiscalDelete = (chaveNfe?: string) => {
        setFormRequestNotasDto((prevData) => prevData.filter(item => item.chaveNfe !== chaveNfe));
        setConferenciasPorChave((prevData) => Object.fromEntries(Object.entries(prevData).filter(([chave]) => chave !== chaveNfe)));
    };

    const handleGeralAdd = async () => {
        if (!formRequestFobDto) return;
        if (formRequestNotasDto.length < 1) {
            message.warning("Deve conter pelo menos 1 Nota");
            return
        }

        const hasFOBInNumeroConhecimentoEqual = formRequestGeralDto.some(request =>
            String(request.numeroConhecimento).trim() === String(formRequestFobDto.numeroConhecimento).trim()
        );


        if (formRequestFobDto.tipoConhecimento === 'FOB' && hasFOBInNumeroConhecimentoEqual) {
            message.warning("Conhecimento já adicionado!!");
            return;
        }

        const updatedGeralRequestDto = {...formRequestFobDto, notas: formRequestNotasDto}
        setFormRequestGeralDto(prevState => [...prevState, updatedGeralRequestDto]);
        setFormRequestNotasDto([]);
        setConferenciasPorChave({});

        cleanForm();
        setGuiaProximoCifFobAberto(false);
        setGuiaProximoFinalizarAberto(true);

    };

    const handleGeralDelete = (numeroConhecimento?: number) => {
        setFormRequestGeralDto((prevData) => prevData.filter(item => item.numeroConhecimento !== numeroConhecimento));
    };

    const handleGeralFinish = async () => {
        if (formRequestGeralDto.length < 1) {
            message.warning("Deve conter pelo menos 1 Nota");
            return
        }
        setGuiaProximoFinalizarAberto(false);
        const updatedRequestDto = {...formRequest, volumeCargaTotal: null, agendas: formRequestGeralDto}

        try {
            await cadastrarMutation.mutateAsync(updatedRequestDto);
            message.success("Cadastro finalizado com sucesso!!")
            navigate(`/`);
        } catch (error) {
            message.error(resolverMensagemErro(error, "Ocorreu um erro ao realizar o cadastro"))
            navigate(`/`);
        }
    };

    const columnsListNFUpload: TableColumnsType<AgendaExternaCifDto> = [
        {
            title: 'Chave NF',
            dataIndex: 'chaveNfe',
            key: 'chaveNfe',
        },
        {
            title: 'Razão social',
            dataIndex: 'razaoSocialFornecedor',
            key: 'razaoSocialFornecedor',
        },
        {
            title: 'Emissão',
            dataIndex: 'dataEmissao',
            key: 'dataEmissao',
            render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : ''
        },
        {
            title: 'Volume',
            dataIndex: 'volumeCarga',
            key: 'volumeCarga',
        },
        {
            title: 'Destinatário',
            key: 'destinatario',
            render: (_, record) => renderMarcaConferencia(record.chaveNfe),
        },
        {
            title: 'Ações',
            key: 'actionsNotaFiscal',
            render: (_, record) => (
                <Flex align={'center'} gap={8} wrap={true}>
                    {resolverMarcaDaNota(record.chaveNfe) === 'RESERVADA_SEM_XML' && (
                        <Tooltip title="Anexar o XML converte para conferida sem refazer os dados">
                            <Button icon={<UploadOutlined/>} type="primary" size="small"
                                    onClick={() => handleAnexarXmlNotaReservada(record.chaveNfe)}>
                                Anexar XML
                            </Button>
                        </Tooltip>
                    )}
                    <Popconfirm
                        title="Tem certeza de que deseja excluir?"
                        onConfirm={() => handleNotaFiscalDelete(record.chaveNfe)}
                        okText="Sim"
                        cancelText="Não"
                    >
                        <Button icon={<DeleteOutlined className={"delete-outline-icon-class"}/>} size="small"
                                aria-label={`Remover a nota ${record.razaoSocialFornecedor ?? record.numeroNota ?? ''} da lista`}>
                            Remover
                        </Button>
                    </Popconfirm>
                </Flex>
            ),
        },
    ]

    const columnsListNotasUpload: TableColumnsType<AgendaExternaFobDto> = [
        {
            title: 'Número de Conhecimento',
            dataIndex: 'numeroConhecimento',
            key: 'numeroConhecimento',
        },
        {
            title: 'Número da Nota',
            key: 'numeroNota',
            render: (_, record) => {
                return record.notas && record.notas.length > 0
                    ? record.notas.map(nota => nota.numeroNota).join(', ')
                    : '-';
            }
        },
        {
            title: 'Tipo de Conhecimento',
            dataIndex: 'tipoConhecimento',
            key: 'tipoConhecimento',
        },
        {
            title: 'Ações',
            key: 'actionsCifFob',
            render: (_, record) => (
                <Popconfirm
                    title="Tem certeza de que deseja excluir?"
                    onConfirm={() => handleGeralDelete(record.numeroConhecimento)}
                    okText="Sim"
                    cancelText="Não"
                >
                    <Button icon={<DeleteOutlined className={"delete-outline-icon-class"}/>} size="small"
                            aria-label={`Remover o bloco ${record.tipoConhecimento ?? ''} ${record.numeroConhecimento ?? ''} da lista`}>
                        Remover
                    </Button>
                </Popconfirm>
            ),
        },
    ]

    const renderStepBar = () => (
        <nav className="step-bar" aria-label="Etapas do agendamento">
            {ETAPAS_AGENDAMENTO.map((etapa, indice) => {
                const isEtapaAtual = indice === etapaAtual;
                const isEtapaConcluida = indice < etapaAtual;
                const classeChip = [
                    'step-bar__chip',
                    isEtapaAtual && 'step-bar__chip--atual',
                    isEtapaConcluida && 'step-bar__chip--concluida',
                ].filter(Boolean).join(' ');
                return (
                    <Fragment key={etapa}>
                        {indice > 0 && <span className="step-bar__separador" aria-hidden="true">›</span>}
                        <span className={classeChip} aria-current={isEtapaAtual ? 'step' : undefined}>
                            <span className="step-bar__indice">{isEtapaConcluida ? <CheckOutlined/> : indice + 1}</span>
                            {etapa}
                        </span>
                    </Fragment>
                );
            })}
        </nav>
    );

    const renderMarcaConferencia = (chaveNfe?: string) => {
        switch (resolverMarcaDaNota(chaveNfe)) {
            case 'RESERVADA_SEM_XML':
                return (
                    <Flex vertical={true} align={'flex-start'} gap={4}>
                        <Tag color="warning" icon={<ExclamationCircleOutlined/>}>
                            {ufFilialSelecionada} sob sua confirmação
                        </Tag>
                        <Text type="secondary">Sem XML — destinatário não conferido</Text>
                    </Flex>
                );
            case 'CIENCIA_UF_DIVERGENTE':
                return (
                    <Flex vertical={true} align={'flex-start'} gap={4}>
                        <Tag color="warning" variant="outlined" icon={<SwapOutlined/>}>
                            NF de {resolverUfDivergenteDaNota(chaveNfe)} · ciência registrada
                        </Tag>
                        <Text type="secondary">Recebida na filial {ufFilialSelecionada} e transportada até {resolverUfDivergenteDaNota(chaveNfe)}</Text>
                    </Flex>
                );
            case 'CONFERIDA':
                return (
                    <Tag color="success" icon={<CheckCircleOutlined/>}>
                        {ufFilialSelecionada} confere
                    </Tag>
                );
        }
    };

    const renderConfirmacaoFilialSemXml = () => {
        const isConfirmacaoNecessaria = !isFilialSemUf && !isDestinatarioConferido && isChaveNfePreenchida;
        if (!isConfirmacaoNecessaria) return null;
        return (
            <Alert
                type="warning"
                showIcon={true}
                title="Sem o XML, quem responde pela filial é você"
                description={
                    <Flex vertical={true} align={'flex-start'} gap={8}>
                        <Text>A chave de acesso sozinha não informa o destinatário. Anexe o XML, use a pesquisa automática pela chave ou confirme abaixo e siga.</Text>
                        <Checkbox checked={isFilialConfirmadaPeloUsuario} onChange={handleConfirmacaoFilialChange}>
                            Confirmo que esta NF é destinada à filial <Text strong={true}>{ufFilialSelecionada}</Text>.
                        </Checkbox>
                        <Text type="secondary">Ao confirmar, <strong>Adicionar NF</strong> é liberado e a nota entra na lista marcada como reservada. Se a filial estiver errada, a carga é recusada na portaria e o agendamento precisa ser refeito.</Text>
                    </Flex>
                }
            />
        );
    };

    const renderConferenciaDestinatario = () => (
        <div className="nf-check-stack">
            {isFilialSemUf && (
                <Alert
                    type="error"
                    role="alert"
                    showIcon={true}
                    title="Não foi possível determinar a UF da filial selecionada"
                    description={
                        <Flex vertical={true} align={'flex-start'} gap={8}>
                            <Text>{MENSAGEM_FILIAL_SEM_UF}</Text>
                            <Text type="secondary">Volte para a tela de agendamentos e selecione a filial novamente.</Text>
                        </Flex>
                    }
                    action={<Button icon={<SwapOutlined/>} onClick={handleTrocarFilial}>Trocar filial</Button>}
                />
            )}
            {isDestinatarioDivergente && (
                <>
                    <span className="nf-file">
                        <FileTextOutlined aria-hidden="true"/>
                        <span>{origemXmlNotaFiscal} — <strong className="nf-file__situacao">{situacaoXmlDivergente}</strong></span>
                    </span>
                    <div className="ciencia-uf" role="group" aria-labelledby="ciencia-uf-titulo">
                        <WarningOutlined className="ciencia-uf__icone" aria-hidden="true"/>
                        <div className="ciencia-uf__corpo">
                            <h3 className="ciencia-uf__titulo" id="ciencia-uf-titulo">A UF desta NF não é a da filial selecionada</h3>
                            <p className="ciencia-uf__lead">
                                Em alguns casos a nota é recebida na filial <strong>{ufFilialSelecionada}</strong> e a carga é transportada depois até o destinatário em <strong>{ufDestinatarioNf}</strong>. Se este for o caso, registre sua ciência abaixo para adicionar a NF.
                            </p>
                            <Flex gap={8} wrap={true}>
                                <Tag>Filial selecionada: <Text strong={true}>{ufFilialSelecionada}</Text></Tag>
                                <Tag>Destinatário da NF: <Text strong={true}>{ufDestinatarioNf}</Text></Tag>
                            </Flex>
                            <div className="ciencia-uf__confirmacao">
                                <Checkbox checked={isCienciaUfDivergenteRegistrada} onChange={handleCienciaUfDivergenteChange}>
                                    <span className="ciencia-uf__rotulo">
                                        Estou ciente de que esta NF é de <strong>{ufDestinatarioNf}</strong> e quero agendar na filial <strong>{ufFilialSelecionada}</strong> mesmo assim.
                                    </span>
                                </Checkbox>
                            </div>
                            <Text type="secondary">Ao marcar, <strong>Adicionar NF</strong> é liberado e a nota entra na lista marcada como recebida com ciência de UF divergente.</Text>
                            <div className="ciencia-uf__alternativas">
                                <span className="ciencia-uf__alternativas-rotulo">Se foi engano:</span>
                                <Tooltip title={isDestinatarioDeFilialConhecida ? undefined : `Não há filial Diferpan em ${ufDestinatarioNf}. Troque a filial na tela de agendamentos.`}>
                                    <Button icon={<SwapOutlined/>} onClick={handleAgendarNaFilialDoDestinatario} disabled={!isDestinatarioDeFilialConhecida}>
                                        Agendar na filial {ufDestinatarioNf}
                                    </Button>
                                </Tooltip>
                                <Button icon={<UploadOutlined/>} onClick={handleTrocarXmlNotaFiscal}>
                                    Trocar o XML
                                </Button>
                                <Text type="secondary">Os dados já preenchidos do agendamento são mantidos.</Text>
                            </div>
                        </div>
                    </div>
                </>
            )}
            {isDestinatarioConfere && (
                <div>
                    <Tag color="success" icon={<CheckCircleOutlined/>}>
                        Destinatário do XML confere com a filial {ufFilialSelecionada}
                    </Tag>
                </div>
            )}
        </div>
    );

    return (
        <div className="fluxo-agendamento">
            {renderStepBar()}
            <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={renderMarcadorObrigatorio} className="fluxo-agendamento__form">
                <Flex vertical={true} gap={16}>
                    <section className="section-card" aria-label="Dados para o agendamento">
                        <div className="section-card__head">
                            <h2 className="section-card__title">Dados para o agendamento</h2>
                            <span className="section-card__hint">Todos os campos são obrigatórios</span>
                        </div>
                        <div className="section-card__body">
                            <div ref={refSelectFornecedor} className="form-field">
                                <Form.Item name="codeSupplierOrTransport" label={rotuloParceiro}
                                           rules={[
                                               {required: true, message: 'Campo obrigatório!'}
                                           ]}>
                                    <Select
                                        placeholder={placeholderSelectSupplierOrTransport}
                                        showSearch={{optionFilterProp: 'label'}}
                                        loading={parceirosQuery.isLoading}
                                        options={codeSupplierOrTransportData}
                                    />
                                </Form.Item>
                            </div>
                            <div ref={refHoraEntrega} className="form-field">
                                <Form.Item name="horaEntrega" label="Hora de entrega" extra={DICA_JANELAS_ENTREGA}
                                           rules={[{required: true, message: 'Campo obrigatório!'}, {validator: validarHorarioEntrega}]}>
                                    <TimePicker
                                        placeholder={FORMATO_HORA}
                                        format={FORMATO_HORA}
                                        minuteStep={PASSO_MINUTOS_ENTREGA}
                                        disabledTime={resolverHorasEMinutosDesabilitados}
                                        hideDisabledOptions={true}
                                        needConfirm={false}
                                        showNow={false}
                                    />
                                </Form.Item>
                            </div>
                            <div ref={refTipoCaminhao} className="form-field">
                                <Form.Item name="tipoCaminhao" label="Tipo de caminhão" extra="Apenas um tipo por agendamento"
                                           rules={[{required: true, message: 'Campo obrigatório!'}]}>
                                    <Select placeholder="Tipo de caminhão" options={TRANSPORT_OPTIONS}/>
                                </Form.Item>
                            </div>
                        </div>
                        <div ref={refSwitchCifFob} className="frete-toggle">
                            <span className="frete-toggle__rotulo"><strong>CIF</strong> / <strong>FOB</strong></span>
                            <Switch
                                size="small"
                                onChange={handleTipoFreteChange}
                                checked={isFob}
                                aria-label="Alternar entre CIF e FOB"
                            />
                            <span className="section-card__hint">{dicaTipoFrete}</span>
                        </div>
                    </section>

                    {isFob && (
                        <section ref={refUploadConhecimento} className="section-card" aria-label="Dados do Conhecimento de Frete">
                            <div className="section-card__head">
                                <h2 className="section-card__title">Preencha os dados do Conhecimento de Frete</h2>
                                <span className="section-card__hint">Exigido porque o frete é FOB</span>
                            </div>
                            <div className="section-card__body">
                                <div className="section-card__coluna">
                                    <UploadPdfOuXml
                                        ref={CTOUploadRef}
                                        onUpload={handleFileUploadForFOB}
                                        onDelete={handleFileDeleteForFOB}
                                        maxCount={2}
                                        disabled={!isFob}
                                        infoText={"Arquivos XML e PDF do C. de Frete (FOB)"}
                                        className="upload-area"
                                    />
                                </div>
                                <div className="form-field">
                                    <Form.Item name="numeroConhecimento" label="Número conhecimento" extra="Somente números" required={true}
                                               normalize={normalizarSomenteDigitos}
                                               rules={[{validator: validateNumeroConhecimento}]}>
                                        <Input placeholder="Número conhecimento" inputMode="numeric" disabled={!isFob}/>
                                    </Form.Item>
                                    <p className="form-field__nota">Lido do XML anexado. Um mesmo conhecimento só pode entrar uma vez na lista de CIF/FOB.</p>
                                </div>
                            </div>
                        </section>
                    )}

                    <section className="section-card" aria-label="Dados da Nota Fiscal">
                        <div className="section-card__head">
                            <h2 className="section-card__title">Preencha os dados da nota fiscal</h2>
                            <div className="section-card__aside">
                                <span className="section-card__hint">Anexe o XML para preencher os campos automaticamente</span>
                                <span className="filial-tag">
                                    <ShopOutlined/> Filial selecionada: <strong className="filial-tag__uf">{ufFilialSelecionada ?? 'não identificada'}</strong>
                                </span>
                                <Button type="link" size="small" onClick={handleTrocarFilial}>Trocar filial</Button>
                            </div>
                        </div>
                        <div className="section-card__body">
                            <div ref={refUploadNotaFiscal} className="section-card__coluna">
                                <UploadPdfOuXml
                                    ref={NFUploadRef}
                                    onUpload={handleFileUploadForCIF}
                                    onDelete={handleFileDeleteForCIF}
                                    maxCount={2}
                                    infoText={infoTextNotaFiscal}
                                    className="upload-area"
                                />
                                {renderConferenciaDestinatario()}
                            </div>

                            <div ref={refChaveNfe} className="form-field">
                                <div className="form-field__compacto">
                                    <Form.Item name="chaveNfe" label="Chave de acesso NF" extra={`${String(chaveNfe ?? "").length}/44`}
                                               normalize={normalizarSomenteDigitos}
                                               rules={[
                                                   {required: true, message: 'Campo obrigatório!'},
                                                   {
                                                       len: 44,
                                                       message: 'O campo deve conter 44 dígitos!',
                                                   },
                                               ]}>
                                        <Input placeholder="Chave de acesso NF" inputMode="numeric" maxLength={44}/>
                                    </Form.Item>
                                    <Tooltip title="Pesquisa automática pela chave de acesso">
                                        <Button icon={<SearchOutlined/>} onClick={handleNfeGetMaisDados}
                                                loading={buttonMaisDadosLoading} type="primary"
                                                className="form-field__botao-busca"/>
                                    </Tooltip>
                                </div>
                                <p className="form-field__nota">A lupa busca o XML da nota pela chave de acesso. É esse XML que traz o destinatário para conferir com a filial <strong>{ufFilialSelecionada ?? 'selecionada'}</strong>.</p>
                                {renderConfirmacaoFilialSemXml()}
                            </div>
                            <div className="form-field">
                                <Form.Item name="razaoSocialFornecedor" label="Razão social"
                                           rules={[{required: true, message: 'Campo obrigatório!'}]}>
                                    <Input placeholder="Digite a razão social"/>
                                </Form.Item>
                            </div>
                            <div className="form-field">
                                <Form.Item name="dataEmissao" label="Data de emissão" rules={[{required: true, message: 'Campo obrigatório!'}]}>
                                    <DatePicker placeholder="Data de emissão" format={FORMATO_DATA}/>
                                </Form.Item>
                            </div>
                            <div className="form-field">
                                <Form.Item name="volumeCarga" label="Volume total da carga" normalize={normalizarSomenteDigitos}
                                           rules={[{required: true, message: 'Campo obrigatório!'}]}>
                                    <Input placeholder="Volume total da carga" inputMode="numeric"/>
                                </Form.Item>
                            </div>
                        </div>
                        <div className="section-card__actions">
                            {isAdicionarNfBloqueado && <span className="section-card__actions-note">{resolverMotivoBloqueioNf()}</span>}
                            <div ref={refBotaoAdicionarNf}>
                                <Button type="primary" htmlType="submit" icon={<PlusOutlined/>} disabled={isAdicionarNfBloqueado} className="botao-acao-class">
                                    Adicionar NF
                                </Button>
                            </div>
                        </div>
                    </section>

                    <section className="section-card" aria-label="Lista de notas fiscais">
                        <div className="section-card__head">
                            <h2 className="section-card__title">Lista de notas fiscais</h2>
                            <span className="section-card__hint">{resolverDicaListaNotas()}</span>
                        </div>
                        <div ref={refTabelaNotas} className="section-card__tabela">
                            <Table
                                dataSource={formRequestNotasDto}
                                columns={columnsListNFUpload}
                                rowKey="chaveNfe"
                                rowClassName={(record) => resolverClasseLinhaNota(record.chaveNfe)}
                                bordered={false}
                                pagination={false}
                                locale={{emptyText: <Empty description="Nenhuma nota fiscal adicionada" image={Empty.PRESENTED_IMAGE_SIMPLE}/>}}
                            />
                        </div>
                        <div className="section-card__actions">
                            <span className="section-card__actions-note">
                                {hasNotasNaLista
                                    ? `As ${formRequestNotasDto.length} ${pluralizar(formRequestNotasDto.length, 'nota acima entra', 'notas acima entram')} no próximo bloco ${isFob ? 'FOB' : 'CIF'}.`
                                    : 'Adicione ao menos uma nota para registrar um bloco CIF/FOB.'}
                            </span>
                            <div ref={refBotaoAdicionarCifFob}>
                                <Button type="primary" icon={<FolderAddOutlined/>} onClick={handleGeralAdd} className="botao-acao-class">
                                    Adicionar CIF/FOB
                                </Button>
                            </div>
                        </div>
                    </section>
                </Flex>
            </Form>

            <section className="section-card" aria-label="Lista de CIF/FOB">
                <div className="section-card__head">
                    <h2 className="section-card__title">Lista de CIF/FOB</h2>
                    <span className="section-card__hint">
                        {hasBlocosCifFob
                            ? `${formRequestGeralDto.length} ${pluralizar(formRequestGeralDto.length, 'bloco adicionado', 'blocos adicionados')}`
                            : 'Nenhum bloco adicionado'}
                    </span>
                </div>
                <div className="section-card__tabela">
                    <Table
                        dataSource={formRequestGeralDto}
                        columns={columnsListNotasUpload}
                        rowKey="numeroConhecimento"
                        bordered={false}
                        pagination={false}
                        locale={{
                            emptyText: (
                                <div className="section-card__empty">
                                    <InboxOutlined style={{fontSize: 28}}/>
                                    <span className="section-card__empty-title">Nenhum CIF/FOB adicionado</span>
                                    <span className="section-card__empty-next">Use <strong>Adicionar CIF/FOB</strong> acima para agrupar as notas. É esse bloco que libera a finalização.</span>
                                </div>
                            ),
                        }}
                    />
                </div>
            </section>

            <div className="commit-bar">
                <div className="commit-bar__resumo">
                    <span><span className="commit-bar__forte">{totalNotas}</span> {pluralizar(totalNotas, 'nota fiscal', 'notas fiscais')}</span>
                    {totalReservadas > 0 && (
                        <span><span className="commit-bar__forte">{totalReservadas}</span> {pluralizar(totalReservadas, 'reservada', 'reservadas')} sem XML</span>
                    )}
                    {totalComCienciaUf > 0 && (
                        <span><span className="commit-bar__forte">{totalComCienciaUf}</span> com ciência de UF divergente</span>
                    )}
                    <span><span className="commit-bar__forte">{totalVolumes}</span> volumes</span>
                    <span><span className="commit-bar__forte">{formRequestGeralDto.length}</span> {pluralizar(formRequestGeralDto.length, 'bloco', 'blocos')} CIF/FOB</span>
                </div>
                <div ref={refBotaoFinalizar} className="commit-bar__acoes">
                    <Button type="primary" onClick={handleGeralFinish} icon={<CheckOutlined/>} loading={cadastrarMutation.isPending} className="botao-acao-class">
                        Finalizar Agendamento
                    </Button>
                </div>
            </div>

            <GuidedTour
                tourAberto={tourAberto}
                abrirTour={abrirTour}
                fecharTour={fecharTour}
                passos={passosTour}
            />
            <Tour
                open={guiaProximoCifFobAberto}
                onClose={() => setGuiaProximoCifFobAberto(false)}
                onFinish={() => setGuiaProximoCifFobAberto(false)}
                steps={passosGuiaProximoCifFob}
                rootClassName="guided-tour-root-class"
            />
            <Tour
                open={guiaProximoFinalizarAberto}
                onClose={() => setGuiaProximoFinalizarAberto(false)}
                onFinish={() => setGuiaProximoFinalizarAberto(false)}
                steps={passosGuiaProximoFinalizar}
                rootClassName="guided-tour-root-class"
            />
            <input
                ref={inputXmlConferenciaRef}
                type="file"
                accept=".xml,text/xml,application/xml"
                aria-label="Anexar XML para conferir o destinatário da Nota Fiscal"
                tabIndex={-1}
                hidden={true}
                onChange={handleXmlConferenciaChange}
            />
        </div>
    );
};
